import React from "react"
import PropTypes from "prop-types"
import _ from "lodash"
import { Button, TextField, Alert, Collapse } from "@mui/material"
import { Add, ForumOutlined } from "@mui/icons-material"
import {
    useGetLectureQuestionsQuery,
    useAskLectureQuestionMutation,
    useGetQuestionThreadQuery,
    useAnswerQuestionMutation,
    useDeleteMyQuestionMutation,
    useDeleteMyAnswerMutation
} from "../../Redux/actions/lecture.action"
import QuestionItem from "./QuestionItem"
import QuestionThreadDialog from "./QuestionThreadDialog"

const PAGE_SIZE = 10

/** One page of a lecture's questions; pages share the RTK Query cache. */
function QuestionPage({ lecture_id, page, onOpen }) {
    const { data } = useGetLectureQuestionsQuery({
        lecture_id,
        offset: page * PAGE_SIZE,
        limit: PAGE_SIZE
    })
    return _.get(data, "list", []).map((question) => (
        <QuestionItem
            key={question.question_id}
            question={question}
            onOpen={onOpen}
        />
    ))
}

QuestionPage.propTypes = {
    lecture_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    page: PropTypes.number.isRequired,
    onOpen: PropTypes.func.isRequired
}

/**
 * The lecture's discussion for enrolled students: ask a question, browse the
 * others (newest first, paged with "load more") and open a thread to read
 * and reply. Students may delete only their own posts.
 */
export default function LectureQA({ lecture_id }) {
    const [pages, setPages] = React.useState(1)
    const [asking, setAsking] = React.useState(false)
    const [form, setForm] = React.useState({ title: "", body: "" })
    const [formError, setFormError] = React.useState("")
    const [openId, setOpenId] = React.useState(null)

    /* Another lecture starts on its own first page. */
    React.useEffect(() => {
        setPages(1)
        setAsking(false)
        setOpenId(null)
    }, [lecture_id])

    const firstPage = useGetLectureQuestionsQuery({
        lecture_id,
        offset: 0,
        limit: PAGE_SIZE
    })
    const total = _.get(firstPage, "data.total", 0)

    const [askAction, askState] = useAskLectureQuestionMutation()
    const [answerAction] = useAnswerQuestionMutation()
    const [deleteQuestionAction] = useDeleteMyQuestionMutation()
    const [deleteAnswerAction] = useDeleteMyAnswerMutation()

    const threadQuery = useGetQuestionThreadQuery(
        { question_id: openId },
        { skip: !openId }
    )

    const submitQuestion = async () => {
        const title = form.title.trim()
        const body = form.body.trim()
        if (!title || !body) {
            setFormError("Add a short title and describe your question.")
            return
        }
        setFormError("")
        try {
            await askAction({ lecture_id, title, body }).unwrap()
            setForm({ title: "", body: "" })
            setAsking(false)
        } catch (error) {
            setFormError(
                _.get(error, "data.detail", null) ||
                    "Could not post your question."
            )
        }
    }

    return (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
            <div className="flex items-center justify-between gap-3">
                <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800">
                    <ForumOutlined sx={{ fontSize: 20 }} />
                    Questions & answers
                    {total > 0 && (
                        <span className="text-sm font-normal text-slate-400">
                            ({total})
                        </span>
                    )}
                </h2>
                {!asking && (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => setAsking(true)}
                    >
                        Ask
                    </Button>
                )}
            </div>

            <Collapse in={asking} unmountOnExit>
                <div className="mt-4 space-y-3 rounded-xl bg-slate-50 p-3 sm:p-4">
                    <TextField
                        fullWidth
                        size="small"
                        label="Title"
                        placeholder="e.g. Why does the formula use the square?"
                        value={form.title}
                        inputProps={{ maxLength: 300 }}
                        onChange={(event) =>
                            setForm((prev) => ({
                                ...prev,
                                title: event.target.value
                            }))
                        }
                    />
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        size="small"
                        label="Your question"
                        value={form.body}
                        inputProps={{ maxLength: 5000 }}
                        onChange={(event) =>
                            setForm((prev) => ({
                                ...prev,
                                body: event.target.value
                            }))
                        }
                    />
                    {formError && <Alert severity="error">{formError}</Alert>}
                    <div className="flex justify-end gap-2">
                        <Button
                            onClick={() => {
                                setAsking(false)
                                setFormError("")
                            }}
                            disabled={askState.isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={submitQuestion}
                            disabled={askState.isLoading}
                        >
                            Post question
                        </Button>
                    </div>
                </div>
            </Collapse>

            {firstPage.isError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {_.get(firstPage, "error.data.detail", null) ||
                        "Questions could not be loaded."}
                </Alert>
            )}

            {firstPage.isSuccess && total === 0 && !asking && (
                <p className="mt-4 rounded-xl border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
                    No questions yet. Stuck on something? Ask the first one.
                </p>
            )}

            <div className="mt-4 space-y-2">
                {_.range(pages).map((page) => (
                    <QuestionPage
                        key={page}
                        lecture_id={lecture_id}
                        page={page}
                        onOpen={(question) => setOpenId(question.question_id)}
                    />
                ))}
            </div>

            {pages * PAGE_SIZE < total && (
                <div className="mt-3 flex justify-center">
                    <Button size="small" onClick={() => setPages(pages + 1)}>
                        Load more questions
                    </Button>
                </div>
            )}

            <QuestionThreadDialog
                open={Boolean(openId)}
                thread={threadQuery.currentData}
                isLoading={threadQuery.isFetching && !threadQuery.currentData}
                error={
                    threadQuery.isError
                        ? _.get(threadQuery, "error.data.detail", null) ||
                          "This question could not be loaded."
                        : ""
                }
                replyLabel="Reply"
                canDelete={(item) => Boolean(item.is_mine)}
                onClose={() => setOpenId(null)}
                onReply={(body) =>
                    answerAction({ question_id: openId, body }).unwrap()
                }
                onDeleteQuestion={async () => {
                    await deleteQuestionAction({ question_id: openId }).unwrap()
                    setOpenId(null)
                }}
                onDeleteAnswer={(answer) =>
                    deleteAnswerAction({ answer_id: answer.answer_id }).unwrap()
                }
            />
        </section>
    )
}

LectureQA.propTypes = {
    lecture_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired
}
